import {trim, get} from 'lodash';
import {isNullOrUndefined} from 'util';
import {ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs/Subscription';
import {OptionDialogStaticAccessor} from './option.dialog.accessor';
import {InputDialogConfig, OptionDialogConfig, OptionDialogText, OptionDialogType} from './option.dialog';

export interface OptionDialogController {
    id: string,
    opts: OptionDialogConfig;
    inputOpts?: InputDialogConfig;
    onApply: (value?: any) => void;
    onCancel: (value?: any) => void;
}

@Component({
    selector: 'tfl-option-dialog',
    template: `
        <div *ngIf="visible" [id]="dialogId" class="modal fade in" role="dialog" style="display: block;">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title">{{ title | translate }}</h4>
                    </div>

                    <div class="modal-body">
                        <p *ngFor="let line of descriptionLines" class="option-dialog-description">
                            {{ line.key | translate:line.params }}
                        </p>

                        <h5 [innerHtml]="message | translate"></h5>

                        <div *ngIf="warning" class="alert alert-warning option-dialog-warning">
                            {{ warning.key | translate:warning.params }}
                        </div>

                        <div *ngIf="isInputDialogType()">
                            <!-- TODO Mikhail add input field customization -->
                            <input id="{{ dialogId + '-input' }}" class="form-control" type="text" [(ngModel)]="value"
                                   required maxlength="{{maxlength}}" autocomplete="off"/>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button id="{{ dialogId + '-confirm' }}"
                                type="button" class="btn"
                                [ngClass]="confirmButtonClass"
                                (click)="apply()" [disabled]="isEmptyData()">
                            {{ confirmButtonText | translate }}
                        </button>

                        <button id="{{ dialogId + '-cancel' }}"
                                *ngIf="!isMessageDialogType()"
                                type="button" class="btn btn-default" (click)="cancel()">
                            {{ cancelButtonText | translate }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `
})
export class OptionDialogComponent implements OnInit, OnDestroy {

    // Component ID from template - used to filter which dialog instance should respond
    @Input() id: string;

    // Internal state
    dialogId: string;
    title: string;
    message: string;
    descriptionLines: OptionDialogText[] = [];
    warning: OptionDialogText;
    value: string;
    confirmButtonText: string;
    cancelButtonText: string;
    visible: boolean = false;
    controller: OptionDialogController;
    confirmButtonClass: string = "btn-danger";
    minlength: number = 1;
    maxlength: number = 150;

    private _subscription: Subscription;

    constructor(private _cdr: ChangeDetectorRef) {
    }

    ngOnInit() {
        this._subscription = OptionDialogStaticAccessor.onOptionDialogShow((controller: OptionDialogController) => {
            // Only respond if this dialog instance matches the requested ID
            // or if no specific ID was set on this component (backward compatibility)
            if (!!controller && (!this.id || controller.id === this.id)) {
                this.value = controller.opts.value;
                this.controller = controller;
                this.dialogId = controller.id;
                this.title = controller.opts.title;
                this.message = controller.opts.message;
                this.descriptionLines = controller.opts.descriptionLines || [];
                this.warning = controller.opts.warning;
                this.confirmButtonText = controller.opts.confirmButtonText || 'COMMON.CONFIRM';
                this.cancelButtonText = controller.opts.cancelButtonText || 'COMMON.CANCEL';
                this.confirmButtonClass = controller.opts.confirmButtonClass || this.confirmButtonClass;
                this.minlength = get(controller, 'inputOpts.minlength', 1);
                this.maxlength = get(controller, 'inputOpts.maxlength', 150);
                this.visible = true;
                this._cdr.detectChanges();
            }
        })
    }

    ngOnDestroy() {
        if (!!this._subscription) {
            this._subscription.unsubscribe();
        }
    }

    apply() {
        this.controller.onApply(this.value);
        this.visible = false;
        this._cdr.detectChanges();
    }

    cancel() {
        this.controller.onCancel(this.value);
        this.visible = false;
        this._cdr.detectChanges();
    }

    isEmptyData(): boolean {
        if (this.isInputDialogType()) {
            return isNullOrUndefined(this.value)
                || trim(this.value).length < this.minlength;
        }

        return false;
    }

    isConfirmDialogType(): boolean {
        return this.controller.opts.type === OptionDialogType.CONFIRM;
    }

    isInputDialogType(): boolean {
        return this.controller.opts.type === OptionDialogType.INPUT;
    }

    isMessageDialogType(): boolean {
        return this.controller.opts.type === OptionDialogType.MESSAGE;
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/shared/option-dialog/option.dialog.component.ts